import React, { useState, useEffect } from 'react'
import { Users, UserPlus, MoreHorizontal, Crown, Shield, Eye, Trash2, Edit } from 'lucide-react'
import toast from 'react-hot-toast'
import { TeamMemberResponse } from '../../types/teams'
import { teamsService } from '../../services/teamsService'
import SmoothLoadingSpinner from '../common/SmoothLoadingSpinner'
import UserSearchDropdown from '../common/UserSearchDropdown'
import { User } from '../../services/userService'
import { FILL_GRADIENT, LINE_GRADIENT } from '../../constants/gradients'
import { analyticsService } from '../../services/analyticsService'

interface TeamMembersListProps {
  teamId: string
  currentUserRole: string
  currentUserId: string
  onError?: (message: string) => void
  onSuccess?: (message: string) => void
}

const TeamMembersList: React.FC<TeamMembersListProps> = ({
  teamId,
  currentUserRole,
  currentUserId,
  onError,
  onSuccess
}) => {
  const [members, setMembers] = useState<TeamMemberResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [actioningMember, setActioningMember] = useState<string | null>(null)
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null)

  // Role definitions with permissions
  const roles = [
    { value: 'viewer', label: 'Viewer', description: 'Can view team and projects', icon: Eye },
    { value: 'developer', label: 'Creator', description: 'Can manage games and projects', icon: Edit },
    { value: 'admin', label: 'Admin', description: 'Can manage team and members', icon: Shield },
    { value: 'owner', label: 'Owner', description: 'Full access to everything', icon: Crown }
  ]

  const canManageMembers = currentUserRole === 'owner' || currentUserRole === 'admin'

  useEffect(() => {
    loadMembers()
  }, [teamId])

  const loadMembers = async () => {
    try {
      setLoading(true)
      const membersData = await teamsService.getTeamMembers(teamId)
      setMembers(membersData)
    } catch (error) {
      console.error('Failed to load members:', error)
      onError?.('Failed to load team members')
    } finally {
      setLoading(false)
    }
  }

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (!canManageMembers) return

    const member = members.find(m => m.user_id === userId);
    const oldRole = member?.role;

    try {
      setActioningMember(userId)
      await teamsService.updateMemberRole(teamId, userId, newRole)
      
      // Track role change
      analyticsService.track({
        name: 'team_role_changed',
        properties: {
          team_id: teamId,
          from_role: oldRole,
          to_role: newRole,
          user_id: userId
        }
      });
      
      await loadMembers() // Refresh the list
      toast.success(`Member role updated to ${newRole}`)
      onSuccess?.(`Member role updated to ${newRole}`)
    } catch (error) {
      console.error('Failed to update role:', error)
      toast.error('Failed to update member role')
      onError?.('Failed to update member role')
    } finally {
      setActioningMember(null)
    }
  }

  const handleRemoveMember = async (userId: string, username: string) => {
    if (!canManageMembers) return

    const confirmed = window.confirm(`Are you sure you want to remove ${username} from the team?`)
    if (!confirmed) return

    try {
      setActioningMember(userId)
      await teamsService.removeMember(teamId, userId)
      await loadMembers() // Refresh the list
      toast.success(`${username} has been removed from the team`)
      onSuccess?.(`${username} has been removed from the team`)
    } catch (error) {
      console.error('Failed to remove member:', error)
      toast.error('Failed to remove team member')
      onError?.('Failed to remove team member')
    } finally {
      setActioningMember(null)
    }
  }

  const getRoleInfo = (role: string) => {
    return roles.find(r => r.value === role) || roles[0]
  }

  const formatJoinedDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <SmoothLoadingSpinner 
        isLoading={true} 
        size="lg" 
        message="Loading team members..." 
        className="py-12" 
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">
            Team Members
          </h3>
          <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
            {members.length} member{members.length !== 1 ? 's' : ''}
          </p>
        </div>

        {canManageMembers && (
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2"
            style={{ background: FILL_GRADIENT }}
          >
            <UserPlus className="w-5 h-5" />
            Add Member
          </button>
        )}
      </div>

      {/* Members List */}
      {members.length === 0 ? (
        <div className="bg-white dark:bg-dark-surface border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-12 text-center">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text mb-2">
            No team members yet
          </h3>
          <p className="text-gray-500 dark:text-dark-text-muted mb-6">
            Collaborate with others by adding team members
          </p>
          {canManageMembers && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2 mx-auto"
              style={{ background: FILL_GRADIENT }}
            >
              <UserPlus className="w-5 h-5" />
              Add Your First Member
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {members.map((member) => {
            const roleInfo = getRoleInfo(member.role)
            const isCurrentUser = member.user_id === currentUserId
            const RoleIcon = roleInfo.icon

            return (
              <div key={member.id} className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl p-6 hover:shadow-lg transition-all duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    {/* Avatar */}
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 border-2 border-gray-200 dark:border-gray-700 bg-gradient-to-br from-purple-500 to-pink-500"
                    >
                      {member.avatar_url ? (
                        <img 
                          src={member.avatar_url} 
                          alt={member.username || 'User'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                            const parent = e.currentTarget.parentElement
                            if (parent) {
                              parent.innerHTML = `<span class="text-white font-semibold text-xl">${((member.username || 'U').charAt(0).toUpperCase())}</span>`
                            }
                          }}
                        />
                      ) : (
                        <span className="text-white font-semibold text-xl">
                          {((member.username || 'U').charAt(0).toUpperCase())}
                        </span>
                      )}
                    </div>

                    {/* Member Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text truncate">
                          {member.username || 'Unknown User'}
                          {isCurrentUser && (
                            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-dark-text-muted">
                              (you)
                            </span>
                          )}
                        </h4>
                      </div>
                      
                      <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-dark-text-muted">
                        <span className="font-medium flex-shrink-0">Joined {formatJoinedDate(member.joined_at)}</span>
                      </div>
                    </div>

                    {/* Role Badge */}
                    <div className="flex items-center">
                      <div 
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white uppercase"
                        style={{
                          background: member.role === 'owner' 
                            ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' 
                            : member.role === 'admin'
                            ? 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)'
                            : member.role === 'developer'
                            ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                            : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                        }}
                      >
                        <RoleIcon className="w-4 h-4" />
                        <span>{roleInfo.label}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {canManageMembers && !isCurrentUser && (
                    <div className="relative ml-2">
                      <button
                        onClick={() => setShowActionsMenu(showActionsMenu === member.id ? null : member.id)}
                        disabled={actioningMember === member.user_id}
                        className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50 transition-colors"
                      >
                        <MoreHorizontal className="w-5 h-5" />
                      </button>

                      {showActionsMenu === member.id && (
                        <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl shadow-lg z-10">
                          <div className="py-2">
                            <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wide border-b border-gray-100 dark:border-gray-800">
                              Actions
                            </div>
                            {roles
                              .filter(role => role.value !== member.role)
                              .map(role => {
                                const RoleIcon = role.icon
                                return (
                                  <button
                                    key={role.value}
                                    onClick={() => {
                                      handleRoleChange(member.user_id, role.value)
                                      setShowActionsMenu(null)
                                    }}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-start gap-3 transition-colors"
                                  >
                                    <RoleIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-400" />
                                    <div>
                                      <div className="font-semibold text-gray-900 dark:text-dark-text">
                                        {role.label}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-dark-text-muted mt-0.5">
                                        {role.description}
                                      </div>
                                    </div>
                                  </button>
                                )
                              })}
                            
                            <div className="border-t border-gray-100 dark:border-gray-800 mt-1">
                              <button
                                onClick={() => {
                                  handleRemoveMember(member.user_id, member.username || 'User')
                                  setShowActionsMenu(null)
                                }}
                                className="w-full text-left px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                              >
                                <Trash2 className="w-5 h-5" />
                                Remove from team
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <AddMemberModal
          teamId={teamId}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false)
            loadMembers()
            onSuccess?.('Member added successfully')
          }}
          onError={onError}
        />
      )}

      {/* Click outside to close actions menu */}
      {showActionsMenu && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setShowActionsMenu(null)}
        />
      )}
    </div>
  )
}

// Add Member Modal Component
interface AddMemberModalProps {
  teamId: string
  onClose: () => void
  onSuccess: () => void
  onError?: (message: string) => void
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  teamId,
  onClose,
  onSuccess,
  onError
}) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [role, setRole] = useState('developer')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const roles = [
    { value: 'viewer', label: 'Viewer', description: 'Can view team and projects' },
    { value: 'developer', label: 'Creator', description: 'Can manage games and projects' },
    { value: 'admin', label: 'Admin', description: 'Can manage team and members' }
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedUser) return

    try {
      setIsSubmitting(true)
      await teamsService.addMember(
        teamId,
        { user_id: selectedUser.id },
        role
      )
      
      // Track member invitation
      analyticsService.track({
        name: 'team_member_invited',
        properties: {
          team_id: teamId,
          role,
          invite_method: 'username'
        }
      });
      
      const displayName = selectedUser.display_name || selectedUser.username || 'User'
      toast.success(`${displayName} has been added to the team`)
      onSuccess()
    } catch (error: any) {
      console.error('Failed to add member:', error)
      const message = error.response?.data?.message || 'Failed to add team member'
      const displayName = selectedUser.display_name || selectedUser.username || 'User'
      
      // Show specific error messages for common cases
      if (message.includes('already a team member')) {
        toast.error(`${displayName} is already a member of this team`)
      } else if (message.includes('not found')) {
        toast.error('User not found')
      } else if (message.includes('permission')) {
        toast.error('You don\'t have permission to add members')
      } else {
        toast.error(message)
      }
      
      onError?.(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white dark:bg-dark-surface rounded-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Gradient line at top */}
        <div className="h-1" style={{ background: LINE_GRADIENT }} />
        
        <div className="p-8">
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-dark-text mb-2">
            Add Team Member
          </h3>
          <p className="text-gray-500 dark:text-dark-text-muted mb-6">
            Search for a user and assign them a role in your team
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                Select User
              </label>
              <UserSearchDropdown
                onSelect={setSelectedUser}
                placeholder="Search users by username..."
                disabled={isSubmitting}
              />
              {selectedUser && (
                  <div className="mt-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 border-2 border-green-300 dark:border-green-700 bg-gradient-to-br from-purple-500 to-pink-500"
                    >
                      {selectedUser.image_url ? (
                        <img 
                          src={selectedUser.image_url} 
                          alt={selectedUser.display_name || selectedUser.username || 'User'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                            const parent = e.currentTarget.parentElement
                            if (parent) {
                              parent.innerHTML = `<span class="text-white font-semibold text-sm">${((selectedUser.display_name || selectedUser.username || 'U').charAt(0).toUpperCase())}</span>`
                            }
                          }}
                        />
                      ) : (
                        <span className="text-white font-semibold text-sm">
                          {((selectedUser.display_name || selectedUser.username || 'U').charAt(0).toUpperCase())}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-green-800 dark:text-green-200 block truncate">
                        {selectedUser.display_name || selectedUser.username || 'User'}
                      </span>
                      <span className="text-xs text-green-600 dark:text-green-300">
                        will be added to the team
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                Role
              </label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 dark:bg-dark-bg dark:text-dark-text font-medium"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {roles.map(roleOption => (
                  <option key={roleOption.value} value={roleOption.value}>
                    {roleOption.label} - {roleOption.description}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-2">
                Roles determine what actions members can perform in the team
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !selectedUser}
                className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
                style={{ background: FILL_GRADIENT }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Adding...
                  </>
                ) : (
                  'Add Member'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default TeamMembersList 